import { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';

/**
 * Custom hook for fetching user products with pagination and caching
 * Optimized to load products separately from user profile data
 */
export const useUserProducts = (userId) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [nextUrl, setNextUrl] = useState(null);
  
  // Use ref to track ongoing requests and prevent duplicates
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Cache configuration
  const CACHE_KEY = `user_products_${userId}`;
  const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  // Load from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        if (now - timestamp < CACHE_DURATION) {
          setProducts(data.results || []);
          setNextUrl(data.next);
          setHasMore(!!data.next);
          return true; // Cache hit
        }
      }
    } catch (err) {

    }
    return false; // Cache miss
  }, [CACHE_KEY, CACHE_DURATION]);

  // Save to cache
  const saveToCache = useCallback((data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (err) {

    }
  }, [CACHE_KEY]);

  // Fetch function with request deduplication
  const fetchProducts = useCallback(async (url, isLoadMore = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Prevent duplicate requests
    if (fetchingRef.current) {
      return;
    }

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    fetchingRef.current = true;
    abortControllerRef.current = new AbortController();

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      
      if (isLoadMore) {
        setProducts(prev => [...prev, ...(data.results || [])]);
      } else {
        setProducts(data.results || []);
        // Only cache first page results
        saveToCache(data);
      }

      setNextUrl(data.next);
      setHasMore(!!data.next);
      setError(null);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching user products:', err);
        setError(err.message || 'Error loading products');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [userId, saveToCache]);

  // Initial load with cache-first strategy
  useEffect(() => {
    if (!userId) return;

    // Try cache first
    const cacheHit = loadFromCache();
    
    if (cacheHit) {
      setLoading(false);
      // Background refresh
      setTimeout(() => {
        const initialUrl = `${config.backendUrl}/users/${userId}/products/`;
        fetchProducts(initialUrl, false);
      }, 100);
    } else {
      // No cache, fetch immediately
      const initialUrl = `${config.backendUrl}/users/${userId}/products/`;
      fetchProducts(initialUrl, false);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [userId, fetchProducts, loadFromCache]);

  // Load next page
  const fetchNextPage = useCallback(() => {
    if (nextUrl && !loadingMore && hasMore && !fetchingRef.current) {
      fetchProducts(nextUrl, true);
    }
  }, [nextUrl, loadingMore, hasMore, fetchProducts]);

  // Refresh (reload from page 1)
  const refresh = useCallback(async () => {
    if (userId) {
      // Clear cache on manual refresh
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch (err) {

      }
      
      const initialUrl = `${config.backendUrl}/users/${userId}/products/`;
      await fetchProducts(initialUrl, false);
    }
  }, [userId, fetchProducts, CACHE_KEY]);

  return {
    products,
    loading,
    loadingMore,
    hasMore,
    error,
    fetchNextPage,
    refresh,
    setProducts,  // Expose for external updates (like likes)
  };
};
